import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Play } from "lucide-react";
import { useStartCase } from "@/api/mutations/processInstances";
import { useAgentModels } from "@/api/queries/models";
import { ApiError } from "@/api/client";
import { SectionCard } from "@/components/SectionCard";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { fixLabelAus, laufModellOptionen, mitAltwert, modelBasisAus } from "@/lib/modelTarget";
import { CasefieldForm } from "@/components/casefield-form/CasefieldForm";
import {
  alsCasefieldDefs,
  baueNutzlast,
  fehlendePflichtfelder,
  geleerteVorbelegungen,
  vorbelegung,
  type CasefieldWerte,
} from "@/components/casefield-form/casefieldValues";
import type { ProcessInstanceView, TemplateSpecView } from "@/types";

// #303 (UC-2): der manuelle Start — der zweite Nutzer des `CasefieldForm`-Bausteins
// (Konzept §8: EINE Formular-Mechanik).
//
// Die Route existiert seit #301 (UC-3); in der Oberfläche gab es sie nicht. Eine
// Einrichtung mit Auslöser „manuell" war damit fertig konfiguriert und trotzdem nicht
// startbar — der einzige Weg war ein curl.

// #P001 (UC-2/UC-4): Haus-Stil eines nativen Auswahlfelds — kein Radix-Select, damit die Wahl
// ein schlichtes `<select>` bleibt (Review LOW-9: geteilte Konstante statt wörtlicher Kopie).
import { NATIVE_SELECT_CLASS as SELECT_CLASS } from "@/lib/formClasses";

function fehlerText(err: unknown): string {
  const status = err instanceof ApiError ? err.status : 0;
  const code = err instanceof ApiError ? (err.body as { code?: string } | undefined)?.code : undefined;
  // #P001 (UC-4): der Start-Parameter „Modell" ist ebenfalls ein 400 — aber ein ganz anderer
  // Fehler als ein Formularfehler, und wer ihn als solchen liest, sucht an der falschen Stelle.
  // ⚠ #P002 (UC-5, Review 8 LOW-2): der Code hat seither ZWEI Bedeutungen — „nicht (mehr)
  // wählbar" (P001: Slot aus, Whitelist verengt, Admin-Fix) UND „erfüllt die Anforderungen der
  // Vorlage nicht". Der frühere Rat „auf Standard stellen" führt im zweiten Fall oft auf genau
  // das Modell, das ebenfalls nicht erfüllt — der Weg ist die neu geladene, gefilterte Liste.
  if (code === "MODEL_NOT_SELECTABLE") {
    return "Dieses Modell ist für diesen Lauf nicht zugelassen — entweder nicht (mehr) wählbar oder es erfüllt die Anforderungen der Vorlage nicht. Liste neu laden und aus den angebotenen wählen.";
  }
  if (code === "INVALID_CASE_INPUT" || status === 400) {
    return "Die Eingaben passen nicht zur Vorlage — Pflichtfelder und Feldtypen prüfen.";
  }
  if (status === 409) return "Die Einrichtung ist nicht bereit — offene Lücken zuerst schliessen.";
  if (status === 403) return "Keine Berechtigung, diese Einrichtung zu starten.";
  return "Start fehlgeschlagen.";
}

export function ManualStartCard({
  instance,
  spec,
}: {
  instance: ProcessInstanceView;
  spec: TemplateSpecView | undefined;
}) {
  const navigate = useNavigate();
  const start = useStartCase();

  // #P001 (UC-4): das Modell für DIESEN Lauf. Der Agent ist der gebundene bzw. der von der
  // Vorlage fixierte — ohne einen von beiden gibt es die Frage nicht (und es wird nichts geladen).
  const modelAgentId = instance.agentId ?? spec?.fixedAgentId ?? undefined;
  const modelle = useAgentModels(modelAgentId);
  const modelDaten = modelle.data;
  const modelleLaedt = !!modelAgentId && modelle.isLoading;
  // `targets` beschreibt GENAU die wählbaren Namen; fehlt es (älterer Master), bleibt die
  // blosse Namensliste — lieber kein Ziel als ein erfundenes.
  // Geteilte Ableitung (Review UC-5 MED-3) — eine Regel für alle drei Picker.
  const modelBasis = modelBasisAus(modelDaten);
  const modelFix = fixLabelAus(modelDaten);
  // Vorbelegt mit der Wahl der Einrichtung: das ist es, was ohne Zutun passieren würde.
  // ABGELEITET, nicht eingefroren (Review MED-1): die Karte steht auf derselben Seite wie das
  // Instanz-Modell-Feld und wird beim Refetch nicht neu gemountet — ein eingefrorener Startwert
  // schickte nach einer Änderung der Einrichtung still die ALTE Wahl als ausdrücklichen Parameter.
  // `null` = noch nichts gewählt ⇒ Instanz-Wahl; `""` = bewusst „Standard".
  const [wahl, setWahl] = useState<string | null>(null);
  const model = wahl ?? (instance.model ?? "");
  // ⚠ Unter einem Admin-Fix gibt es KEINE Wahl — auch keine vorbelegte. Anders als an der
  // Einrichtung (dort muss die gespeicherte Wahl entfernbar bleiben) ist hier nichts zu
  // speichern: eine mitgeschickte Wahl liefe garantiert in ein 400, und der Lauf würde an
  // einem gesperrten Feld scheitern. Also reist unter dem Fix nichts mit.
  const gewaehltesModell = modelFix !== null ? "" : model;
  // Ein vorbelegter Wert, den die Liste nicht (mehr) kennt, bleibt sichtbar — still
  // verschwinden würde heissen, dass der Start heimlich ein anderes Modell nimmt.
  // `belastbar` nur mit GELADENER Liste (Review MED-5): bei Ladefehler oder 403 (fixierter
  // Service-Agent, Nicht-Admin) wäre „(nicht mehr wählbar)" ein erfundenes Urteil — der Name ist
  // dann womöglich wählbar, und der Master nähme ihn an.
  // #P002 (UC-5): der Master hat die Liste bereits auf „erfüllt alle Schritte" gefiltert
  // (`runModelChoices`). Fehlt das Feld (älterer Master) oder ist es `null` (nicht
  // ermittelbar), bleibt es bei der ungefilterten Basis — nie eine leere Auswahl erfinden.
  const { optionen: laufOptionen, gefiltert: modellGefiltert } = laufModellOptionen(
    instance.runModelChoices,
    modelBasis,
  );
  const modelOptionen = modelFix !== null ? [] : mitAltwert(laufOptionen, model, !!modelDaten);

  // Der Start-KONTRAKT (`input.required`) entscheidet über Pflicht, nicht das deklarative
  // `required` am Feld: es sind zwei Ebenen, und beim Start gilt der Kontrakt.
  const felder = useMemo(
    () =>
      alsCasefieldDefs([...(spec?.input.required ?? []), ...(spec?.input.optional ?? [])], spec, {
        pflicht: spec?.input.required ?? [],
      }),
    [spec],
  );

  // Vorbelegungen der Einrichtung als Startwerte — der Mensch soll sie sehen und
  // überschreiben können, nicht blind erben. Gesetzt wird EINMAL je Vorlagen-Version:
  // ein Bereitschafts-Sweep ändert `updatedAt` und würde sonst mitten im Tippen alles
  // zurücksetzen (dieselbe Falle wie im Konfigurations-Formular, #300 NIT-17).
  const [werte, setWerte] = useState<CasefieldWerte>({});
  const specSchluessel = spec ? `${spec.templateId}@${spec.version}` : "";
  useEffect(() => {
    if (!spec) return;
    setWerte(vorbelegung(felder, instance.inputDefaults));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specSchluessel]);

  const fehlend = fehlendePflichtfelder(felder, werte);
  // #303 (Review L6): geleerte Vorbelegungen. `baueNutzlast` lässt leere Felder weg, und
  // der Master legt die Vorbelegungen der Einrichtung UNTER die mitgeschickten Felder —
  // ein geleertes Feld bekäme also seinen Vorschlag zurück, ohne dass es jemand merkt.
  const geleert = geleerteVorbelegungen(felder, werte, instance.inputDefaults);

  const starte = (dryRun: boolean) => {
    let input: Record<string, string | number | boolean>;
    try {
      // Der leere Text reist AUSDRÜCKLICH mit — nur so überschreibt er den Vorschlag.
      input = { ...baueNutzlast(felder, werte), ...geleert.leer };
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Eingabe ungültig");
      return;
    }
    start.mutate(
      // #P001 (UC-4): nur eine AUSDRÜCKLICHE Wahl reist mit — „" heisst „die Kette gilt".
      { id: instance.id, input, dryRun, ...(gewaehltesModell ? { model: gewaehltesModell } : {}) },
      {
        onSuccess: (lauf) => {
          toast.success(dryRun ? "Probelauf gestartet" : "Lauf gestartet");
          navigate(`/cases/${lauf.id}`);
        },
        onError: (err) => toast.error(fehlerText(err)),
      },
    );
  };

  return (
    <SectionCard
      title="Von Hand starten"
      description="Diese Einrichtung wird nicht von einem Zeitplan ausgelöst — der Start liegt bei Ihnen."
      testId="manual-start"
    >
      <div className="space-y-3">
        {/* #P001 (UC-4): das Modell für diesen einen Lauf. Die Kette darunter kann die
            Oberfläche nicht auflösen (der Plattform-Default ist kein Owner-Read) — womit der
            Lauf wirklich begonnen hat, steht danach am Lauf. */}
        <div className="w-80 space-y-1.5">
          <Label>Modell für diesen Lauf</Label>
          <select
            value={gewaehltesModell}
            onChange={(e) => setWahl(e.target.value)}
            disabled={start.isPending || !modelAgentId || modelleLaedt || modelFix !== null}
            className={SELECT_CLASS}
            data-testid="start-model"
          >
            <option value="">
              {modelleLaedt ? "Modelle werden geladen…" : "Standard (Instanz → Agent → Plattform)"}
            </option>
            {modelOptionen.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {/* #P002 (UC-5): nur wenn die Anforderungen wirklich etwas wegnehmen — sonst wäre
              der Satz ein Hinweis auf eine Regel, die hier nichts bewirkt. */}
          {modellGefiltert && modelFix === null && (
            <p className="text-[12px] text-text3" data-testid="start-model-gefiltert">
              Nur Modelle, die die Anforderungen aller Schritte erfüllen.
            </p>
          )}
          {!modelAgentId && (
            <p className="text-[12px] text-text3" data-testid="start-model-kein-agent">
              Erst einen Agenten binden — welche Modelle wählbar sind, hängt an ihm.
            </p>
          )}
          {modelFix !== null && (
            <p className="text-[12px] text-text3" data-testid="start-model-fix">
              Admin-Fix: {modelFix} — der Admin hat das Modell fixiert, dieser Lauf kann nicht
              abweichen.
            </p>
          )}
          {/* Ein Ladefehler darf den Start nicht verhindern: ohne Wahl gilt die Kette. */}
          {modelAgentId && modelle.isError && (
            <p className="text-[12px] text-warn" data-testid="start-model-fehler">
              Modell-Liste nicht abrufbar — der Lauf startet mit dem Standard.
            </p>
          )}
        </div>
        {felder.length > 0 && (
          <CasefieldForm
            felder={felder}
            werte={werte}
            onChange={setWerte}
            disabled={start.isPending}
            testId="start-form"
          />
        )}
        {fehlend.length > 0 && (
          <p className="text-[12px] text-warn" data-testid="start-pflicht-fehlt">
            Noch auszufüllen: {fehlend.join(", ")}
          </p>
        )}
        {/* Ein leerer Wert ist für Zahl, Datum, Ja/Nein und Auswahl keine gültige Angabe —
            dort lässt sich der Vorschlag der Einrichtung nicht wegnehmen. Das wird GESAGT,
            statt ihn still zurückzuholen (Review L6). */}
        {geleert.nichtLeerbar.length > 0 && (
          <p className="text-[12px] text-warn" data-testid="start-vorschlag-bleibt">
            Für {geleert.nichtLeerbar.join(", ")} gilt weiter der Vorschlag der Einrichtung —
            ein leeres Feld nimmt ihn nicht weg. Zum Ändern einen anderen Wert eintragen.
          </p>
        )}
        <div className="flex items-center gap-2">
          <Button onClick={() => starte(false)} disabled={start.isPending || fehlend.length > 0} data-testid="instance-start">
            <Play className="h-4 w-4" />
            {start.isPending ? "Starte…" : "Lauf starten"}
          </Button>
          {/* Probelauf: Schritte mit Aussenwirkung werden übersprungen (Konzept §13.4).
              Der bequemste Weg, eine frisch eingerichtete Vorlage zu prüfen, ohne dass
              jemand draussen eine Mail bekommt. */}
          <Button
            variant="outline"
            onClick={() => starte(true)}
            disabled={start.isPending || fehlend.length > 0}
            data-testid="instance-start-dry"
          >
            Probelauf (ohne Aussenwirkung)
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}
